/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback, useRef} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {translate} from '@ion/core/src/locale/locale';
import {CreateJob, DownloadJobTasks} from '@ion/core/src/types/entities';
import {CellMenu} from '@ion/dls/src/components/grid/CellMenu';
import {CellMenuItem} from '@ion/dls/src/components/grid/CellMenuItem';
import {DeleteIcon} from '@ion/dls/src/icons/DeleteIcon';
import {RemoveIcon} from '@ion/dls/src/icons/RemoveIcon';
import {SortDownIcon} from '@ion/dls/src/icons/SortDownIcon';
import {SortUpIcon} from '@ion/dls/src/icons/SortUpIcon';
import {dlsColor} from '@ion/dls/src/theme/theme';

// defines the job task row editor prop definitions ...
export interface JobTaskRowEditorProps {
  index: number;
  tasks: CreateJob;
  removeDependency?: Function,
  removeTask: (index: number, jobTasks: DownloadJobTasks) => void,
  navigateRows: (index: number, nav: string, tasks: CreateJob) => void
}

const useStyles = makeStyles(() => ({
  menuButton : {
    paddingLeft : '0px',
    minWidth    : '0px'
  },
  disableItem : {
    opacity : '0.5',
    cursor  : 'default'
  }
}));

/**
 * Defines the JobTaskRowEditor component. This component contains the cell actions
 * for the job tasks table rows.
 *
 * ```typescript
 *
 *   import {JobTaskRowEditor} from './JobTaskRowEditor';
 *
 *   <JobTaskRowEditor .../>
 *
 * ```
 */
export const JobTaskRowEditor = ({
  index,
  tasks,
  removeDependency,
  removeTask,
  navigateRows
}: JobTaskRowEditorProps) => {
  const classes           = useStyles();
  const cellMenuRef       = useRef(null);
  const jobTasks          = tasks?.tasks;
  const dependentTask     = jobTasks[index]?.task?.metaData?.dependency?.index;
  const hasDependentTask  = dependentTask !== undefined;
  const childrenTask      = jobTasks[index]?.task.children;
  const disableMoveUp     = dependentTask === index - 1;
  const disableMoveDown   = childrenTask?.includes(jobTasks[index+1]?.id);
  const fieldTranslations = translate('jobcreate:actionMenuItems', {returnObjects: true});
  const tooltipTitlePath  = translate('jobcreate:dependentTask', {returnObjects: true});

  function closeMenu() {
    cellMenuRef.current.closeCellMenu(cellMenuRef.current, true);
  };

  const onAction = useCallback(function(type: string) {
    return function() {
      closeMenu();
      if (type === 'delete') {
        removeTask(index, jobTasks);
      } else if (type === 'removeDependency') {
        removeDependency({
          tasks     : {tasks: jobTasks},
          taskIndex : index
        });
      } else if (type === 'moveUp' && !disableMoveUp) {
        navigateRows(index, 'up', tasks);
      } else if (type === 'moveDown' && !disableMoveDown) {
        navigateRows(index, 'down', tasks);
      }
    };
  }, [index, tasks]);

  return (
    <CellMenu ref={cellMenuRef} menuItems={
      <div>
        {<CellMenuItem icon={<DeleteIcon />}
          onClick={onAction('delete')}
          label={`${fieldTranslations?.delete}`} />}
        {(hasDependentTask) && <CellMenuItem icon={<RemoveIcon color={dlsColor('grey2')} />}
          onClick={onAction('removeDependency')}
          label={`${fieldTranslations?.removeDependency}`} />}
        {(index > 0) && <CellMenuItem icon={<SortUpIcon />}
          onClick={onAction('moveUp')}
          title={disableMoveUp && tooltipTitlePath?.moveUpWarning}
          customStyle={disableMoveUp && classes.disableItem}
          label={`${fieldTranslations?.moveUp}`} />}
        {(index < jobTasks.length - 1) && <CellMenuItem icon={<SortDownIcon />}
          onClick={onAction('moveDown')}
          title={disableMoveDown && tooltipTitlePath?.insideChildWarning}
          customStyle={disableMoveDown && classes.disableItem}
          label={`${fieldTranslations?.moveDown}`} />}
      </div>
    } className={classes.menuButton} />
  );
};

export default memo(JobTaskRowEditor);
